import React from 'react';
import {
  Box,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { formatCurrency } from '../../../../utils/formatters';
import type { Info360Documento, Info360Indicadores } from '../types';
import type { BcraCentralDeudoresConsulta } from '../../../../types/bcraCentralDeudores';
import Info360DocumentoList from './Info360DocumentoList';
import Info360ChequesDevueltosSection from './Info360ChequesDevueltosSection';

interface Props {
  nroCliente: number;
  data: Info360Indicadores;
  bcraData: BcraCentralDeudoresConsulta | null;
  bcraLoading?: boolean;
  onAbrirDocumento?: (documento: Info360Documento) => void;
}

const Info360TabCredito: React.FC<Props> = ({
  nroCliente,
  data,
  bcraData,
  bcraLoading = false,
  onAbrirDocumento,
}) => {
  const usoPct = data.creditoLimite > 0
    ? Math.min(100, Math.max(0, (data.creditoUtilizado / data.creditoLimite) * 100))
    : 0;
  const agingMax = Math.max(...data.aging.map((a) => a.importe), 1);

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
          Uso del crédito
        </Typography>
        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">Utilizado</Typography>
            <Typography variant="body2" fontWeight={600}>
              {formatCurrency(data.creditoUtilizado)} / {formatCurrency(data.creditoLimite)}
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={usoPct}
            color={usoPct >= 100 ? 'error' : usoPct >= 85 ? 'warning' : 'primary'}
            sx={{ height: 8, borderRadius: 999 }}
            aria-label={`Uso del crédito ${usoPct.toFixed(0)} por ciento`}
          />
          <Typography variant="caption" color="text.secondary">
            Disponible: {formatCurrency(data.creditoDisponible)}
            {' · '}Pagos en término: {data.porcentajePagosEnTermino == null ? 'Sin datos' : `${data.porcentajePagosEnTermino}%`}
            {' · '}Máx. atraso: {data.maximoAtrasoDias == null ? 'Sin datos' : `${data.maximoAtrasoDias} días`}
          </Typography>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
          Antigüedad de deuda (aging)
        </Typography>
        <Grid container spacing={1.5}>
          {data.aging.map((bucket) => (
            <Grid item xs={12} sm={6} md={3} key={bucket.etiqueta}>
              <Box>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">{bucket.etiqueta}</Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {formatCurrency(bucket.importe)}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={(bucket.importe / agingMax) * 100}
                  sx={{ height: 6, borderRadius: 999 }}
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Info360ChequesDevueltosSection
        nroCliente={nroCliente}
        chequesDevueltos={data.chequesDevueltos12Meses}
        bcraData={bcraData}
        bcraLoading={bcraLoading}
      />

      <Info360DocumentoList
        titulo="Documentos abiertos"
        documentos={data.documentosAbiertos}
        emptyText="Sin documentos abiertos."
        onAbrirDocumento={onAbrirDocumento}
      />
      <Info360DocumentoList
        titulo="Últimos pagos"
        documentos={data.ultimosPagos}
        emptyText="Sin pagos recientes."
        onAbrirDocumento={onAbrirDocumento}
      />
    </Stack>
  );
};

export default Info360TabCredito;
